import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDialogListener, useEventHub, useNotify } from '@/composables';
import { useYardTaskMutations } from '@satellite/composables/useYardTaskMutations';

import {
  OpenCreateYardCheckTaskDialogEvent,
  OpenCreateYardMoveTaskDialogEvent
} from '@/composables/useEventBasedDialog';

import type { CheckTaskFormData, MoveTaskFormData } from '@nova/core';

type TaskType =
  | OpenCreateYardCheckTaskDialogEvent['type']
  | OpenCreateYardMoveTaskDialogEvent['type'];
type TaskCreateData = CheckTaskFormData | MoveTaskFormData;

type CreateYardTaskDialogEventName =
  | 'open-create-yard-task-drawer'
  | 'open-create-yard-move-task-drawer';

type UseCreateYardTaskDrawerOptions<K extends CreateYardTaskDialogEventName> = {
  dialogEventName: K;
  defaultTaskType?: TaskType;
};

export function useCreateYardTaskDrawer<K extends CreateYardTaskDialogEventName>(
  options: UseCreateYardTaskDrawerOptions<K>
) {
  const { dialogEventName, defaultTaskType = 'move' } = options;
  const { t } = useI18n();
  const notify = useNotify();
  const eventHub = useEventHub();

  const {
    isDialogOpen,
    dialogProps,
    closeDialog,
    getDialogProp,
    subscribeToDialogEvent,
    unsubscribeFromDialogEvent
  } = useDialogListener<K>(dialogEventName);

  onMounted(() => {
    subscribeToDialogEvent();
  });

  onUnmounted(() => {
    unsubscribeFromDialogEvent();
  });

  const tab = ref<TaskType>(defaultTaskType);
  const isSubmitDisabled = ref(true);
  const currentTaskData = ref<TaskCreateData | null>(null);

  const warehouseId = computed(() => dialogProps.value?.warehouseId || '');
  const warehouseTimezone = computed(() => dialogProps.value?.warehouseTimezone || 'utc');

  const { createTask: createTaskMutation } = useYardTaskMutations({
    warehouseId,
    warehouseTimezone
  });

  const initializeCheckTask = (): CheckTaskFormData => {
    // Type assertion: we know this is a check task, so we can access check-specific properties
    const checkProps = dialogProps.value as OpenCreateYardCheckTaskDialogEvent;
    return {
      type: 'check',
      subject: checkProps?.subject ?? '',
      spot: checkProps?.spot ?? undefined,
      area: checkProps?.area ?? undefined,
      checklist: checkProps?.checklist ?? [],
      category: checkProps?.category ?? undefined
    };
  };

  const initializeMoveTask = (): MoveTaskFormData => {
    // Type assertion: we know this is a move task, so we can access move-specific properties
    const moveProps = dialogProps.value as OpenCreateYardMoveTaskDialogEvent;
    return {
      type: 'move',
      asset: moveProps?.asset ?? undefined,
      originSpot: moveProps?.originSpot ?? undefined,
      destinationSpot: moveProps?.destinationSpot ?? undefined
    };
  };

  const initializeTask = (taskType: TaskType): TaskCreateData => {
    const baseTask = taskType === 'check' ? initializeCheckTask() : initializeMoveTask();
    return {
      ...baseTask,
      assignee: getDialogProp('assignee') ?? undefined,
      dueByDate: getDialogProp('dueByDate') ?? undefined,
      dueByTime: getDialogProp('dueByTime') ?? undefined,
      tags: getDialogProp('tags') ?? []
    } as TaskCreateData;
  };

  watch(
    isDialogOpen,
    newIsOpen => {
      if (!newIsOpen) {
        tab.value = defaultTaskType;
        currentTaskData.value = initializeTask(defaultTaskType);
      } else {
        const taskType = (dialogProps.value?.type ?? defaultTaskType) as TaskType;
        tab.value = taskType;
        currentTaskData.value = initializeTask(taskType);
      }
    },
    { immediate: true }
  );

  watch(tab, () => {
    isSubmitDisabled.value = true;
    currentTaskData.value = initializeTask(tab.value);
  });

  const handleValidate = (isValid: boolean) => {
    isSubmitDisabled.value = !isValid;
  };

  const createTask = async () => {
    if (!dialogProps.value) {
      console.error('Failed to create task: No dialog props found');
      return;
    }

    if (!currentTaskData.value) {
      console.error('Failed to create task: No task data available');
      return;
    }

    try {
      const task = await createTaskMutation(currentTaskData.value);

      notify(t(`yard.messages.${currentTaskData.value.type}TaskCreated`), 'success');

      dialogProps.value.onTaskCreated?.(task);
      eventHub.$emit('create-YardTask', task);
      closeDialog();
    } catch (error) {
      console.error('Failed to create yard task:', error);
      notify(t('yard.messages.failedToCreateYardTask'), 'error');
    }
  };

  return {
    tab,
    isDialogOpen,
    isSubmitDisabled,
    currentTaskData,
    dialogProps,

    getDialogProp,
    closeDialog,
    createTask,
    handleValidate,

    initializeTask,
    initializeCheckTask,
    initializeMoveTask,
    t
  };
}
